import axios from 'axios';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Building2, MapPin, Phone, Clock, XCircle, PauseCircle, FileCheck, Mail, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { LegendeBadgesCRM } from '@/components/crm/LegendeBadgesCRM';
import { STATUT_COLORS, STATUT_LABELS } from '@/lib/statutsConfig';
// 🆕 [Phase 85 Sprint 1 — 2026-06-23] Helpers labels FR-CA pour statuts paiement dérivés.
import {
  shouldShowDepotBadge,
  shouldShowFinalBadge,
  getDepotLabel,
  getFinalLabel,
  getDepotColor,
  getFinalColor,
} from '@/lib/statutsPaiementLabels';
import { usePermissions } from '@/lib/PermissionsContext';
import { useAuth } from '@/lib/AuthContext';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useEmailPreview, EmailPreviewFrame } from '@/components/EmailPreview';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * 🆕 [Phase 85 Sprint 1 — 2026-06-23] Composant des 3 badges statuts CRM.
 *
 * Affiche jusqu'à 3 badges côte-à-côte :
 *   1. Statut principal (vente, ancien `dossier.statut` simplifié) — palette legacy STATUT_COLORS
 *   2. Statut paiement dépôt (si pertinent — masqué si "non_requis")
 *   3. Statut paiement final (si pertinent — masqué si "non_applicable")
 *
 * Source : `/api/crm/dossiers-v2` enrichit chaque dossier avec
 * `statut_principal`, `statut_paiement_depot`, `statut_paiement_final`
 * (calculés dynamiquement depuis db.factures par lib/statuts_paiement.py).
 *
 * Fallback compat : si `statut_principal` est absent (legacy endpoint), on
 * retombe sur `dossier.statut`. Aucun crash possible.
 */
const StatutsBadges = ({ dossier }) => {
  const statutVente = dossier?.statut_principal || dossier?.statut;
  const depot = dossier?.statut_paiement_depot;
  const finalPmt = dossier?.statut_paiement_final;

  // 🔒 [Phase 85 Sprint 2] Si les 3 statuts sont absents (= payload masqué par
  // `apply_statuts_visibility` pour les rôles employés terrain), on ne rend RIEN.
  // Défense en profondeur : la sécurité primaire reste backend.
  if (!statutVente && !depot && !finalPmt) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap gap-1.5 items-center"
      data-testid={`statuts-badges-${dossier?.id || 'unknown'}`}
    >
      <Badge
        className={STATUT_COLORS[statutVente] || 'bg-slate-100 text-slate-700'}
        data-testid={`badge-statut-principal-${dossier?.id || 'unknown'}`}
      >
        {STATUT_LABELS[statutVente] || statutVente || '—'}
      </Badge>
      {shouldShowDepotBadge(depot) && (
        <Badge
          variant="outline"
          className={`${getDepotColor(depot)} border`}
          data-testid={`badge-statut-depot-${dossier?.id || 'unknown'}`}
        >
          <span aria-hidden="true" className="mr-1">💰</span>
          {getDepotLabel(depot)}
        </Badge>
      )}
      {shouldShowFinalBadge(finalPmt) && (
        <Badge
          variant="outline"
          className={`${getFinalColor(finalPmt)} border`}
          data-testid={`badge-statut-final-${dossier?.id || 'unknown'}`}
        >
          <span aria-hidden="true" className="mr-1">💳</span>
          {getFinalLabel(finalPmt)}
        </Badge>
      )}
    </div>
  );
};

export function DossiersCRM() {
  const { canDo } = usePermissions();
  const { user } = useAuth();
  const isTerrainUser = ['chef_equipe', 'peintre'].includes(user?.role);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreDepartement, setFiltreDepartement] = useState('tous');
  const [filtreChef, setFiltreChef] = useState('tous');
  const [filtreDateProd, setFiltreDateProd] = useState('');
  // 🆕 [2026-06-12] Fix cohérence chefs — Liste canonique des chefs ACTUELLEMENT
  // actifs (source : backend `/api/lib/chefs-actifs`). On NE déduit PLUS la liste
  // depuis `dossiers.map(d => d.chef_equipe_nom)` qui incluait les anciens chefs
  // (les vieux dossiers gardent leur snapshot historique).
  const [chefsActifs, setChefsActifs] = useState([]); // [{id, nom_complet}]
  // 🆕 2026-05-01 — Toggle "Afficher les inactifs" (abandonne), persistant via localStorage
  const [showInactifs, setShowInactifs] = useState(() => {
    try { return localStorage.getItem('crm_show_inactifs') === '1'; } catch (_) { return false; }
  });
  // 🆕 [Phase 30 #6] Toggle « Projets Internes » — ON = uniquement BT/dossiers internes
  const [internesOnly, setInternesOnly] = useState(() => {
    try { return localStorage.getItem('crm_internes_only') === '1'; } catch (_) { return false; }
  });

  useEffect(() => { fetchDossiers(); }, [user?.employee_id, showInactifs, internesOnly]);

  // 🆕 [2026-06-12] Fix cohérence chefs — Charge la liste canonique des chefs actifs
  // (source unique : `/api/chefs-actifs` qui pointe vers `lib/chefs_actifs.py`).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/chefs-actifs`);
        if (!cancelled) {
          setChefsActifs(res.data?.chefs || []);
        }
      } catch (e) {
        // Pas bloquant : si l'endpoint échoue, le filtre chef sera vide (au lieu
        // d'inclure les anciens chefs) — c'est volontaire pour éviter les régressions.
        if (!cancelled) setChefsActifs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchDossiers = async () => {
    try {
      // Also trigger auto suivi check
      axios.post(`${API_URL}/api/crm/check-suivi-auto`).catch(() => {});
      const peutVoirTous = canDo('projets_voir_tous');
      // 🆕 [Phase 85 Sprint 1 — 2026-06-23] Bascule vers l'endpoint enrichi v2 qui
      // ajoute statut_principal + statut_paiement_depot + statut_paiement_final
      // calculés dynamiquement depuis db.factures. Ancien /api/crm/dossiers reste
      // disponible (rétrocompat totale) mais n'est plus consommé par cette page.
      let url = `${API_URL}/api/crm/dossiers-v2`;
      const params = [];
      if (!peutVoirTous && user?.employee_id) {
        params.push(`employee_id=${user.employee_id}`);
      }
      if (showInactifs) {
        params.push('inactifs_only=true');
      }
      if (internesOnly) {
        params.push('internes_only=true');
      }
      if (params.length) url += '?' + params.join('&');
      const response = await axios.get(url);
      if (response.status !== 200) throw new Error('Erreur');
      const data = response.data;
      setDossiers(data.dossiers || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const toggleShowInactifs = (val) => {
    setShowInactifs(val);
    try { localStorage.setItem('crm_show_inactifs', val ? '1' : '0'); } catch (_) {}
  };

  // 🆕 [Phase 30 #6] Toggle Projets Internes
  const toggleInternesOnly = (val) => {
    setInternesOnly(val);
    try { localStorage.setItem('crm_internes_only', val ? '1' : '0'); } catch (_) {}
  };

  const changerStatut = async (dossierId, action) => {
    try {
      let url = '';
      if (action === 'en_attente') url = `${API_URL}/api/crm/dossiers/${dossierId}/marquer-suivi-fait`;
      else if (action === 'refuse') url = `${API_URL}/api/crm/dossiers/${dossierId}/marquer-refuse`;
      const res = await axios.patch(url);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(action === 'en_attente' ? 'Mis en attente!' : 'Marqué refusé!');
      fetchDossiers();
    } catch (e) { toast.error('Erreur'); }
  };

  const marquerSoumAFaire = async (dossierId) => {
    const date = dossiersSoumAFaire[dossierId] || '';
    try {
      const res = await axios.patch(`${API_URL}/api/crm/dossiers/${dossierId}/marquer-soum-a-faire`,
        date ? { date_echeance: date } : {}
      );
      if (res.status === 200) {
        toast.success('Dossier marqué "Soumission à faire"!');
        setDossiersSoumAFaire(prev => { const n = { ...prev }; delete n[dossierId]; return n; });
        fetchDossiers();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  // 🆕 2026-04-30 — Bouton "Facture prête à envoyer" (CP) sur les dossiers Finalisés
  const [facturePreteLoading, setFacturePreteLoading] = useState({}); // {dossierId: bool}
  const facturePreteAEnvoyer = async (dossierId) => {
    if (!confirm('Notifier l\'adjoint que la facture est prête à être envoyée au client ?')) return;
    setFacturePreteLoading(prev => ({ ...prev, [dossierId]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/crm/dossiers/${dossierId}/facture-prete-a-envoyer`);
      if (res.status === 200) {
        toast.success('Adjoint notifié — dossier déplacé vers "Envoyer facture"');
        fetchDossiers();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la notification');
    } finally {
      setFacturePreteLoading(prev => { const n = { ...prev }; delete n[dossierId]; return n; });
    }
  };

  const dossiersFiltres = dossiers.filter((d) => {
    const matchSearch =
      (d.nom_dossier || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.client_nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.numero_dossier || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filtreStatut === 'tous' || d.statut === filtreStatut || (filtreStatut === 'en_attente_production' && d.statut === 'attente_production');
    const matchDept = filtreDepartement === 'tous' || d.departement === filtreDepartement;
    const matchChef = filtreChef === 'tous' || d.chef_equipe_nom === filtreChef;
    const matchDate = !filtreDateProd || (d.date_production && d.date_production.startsWith(filtreDateProd));
    return matchSearch && matchStatut && matchDept && matchChef && matchDate;
  });

  // 🛠️ [2026-06-12] Fix cohérence chefs — chefsUniques = liste canonique des chefs
  // ACTUELLEMENT actifs (depuis /api/chefs-actifs). On NE déduit PLUS la liste
  // depuis `dossiers.map(d => d.chef_equipe_nom)` qui incluait les ex-chefs
  // historiques (un chef rétrogradé en peintre apparaissait quand même).
  const chefsUniques = chefsActifs
    .map(c => c.nom_complet)
    .filter(Boolean);

  const [dossiersSoumAFaire, setDossiersSoumAFaire] = useState({}); // { dossierId: dateEcheance }

  const showActions = filtreStatut === 'suivi_a_faire' || filtreStatut === 'en_attente' || filtreStatut === 'soum_a_faire' || filtreStatut === 'soum_a_valider' || filtreStatut === 'prospect' || filtreStatut === 'finalise' || filtreStatut === 'attente_paiement';

  // 🆕 Helpers compteur de jours impayés
  const joursDepuisIso = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const now = new Date();
    return Math.floor((now - d) / 86400000);
  };
  const colorJoursImpayes = (j) => {
    if (j === null) return 'text-slate-500';
    if (j > 30) return 'text-red-700 font-bold';
    if (j > 14) return 'text-amber-600 font-semibold';
    return 'text-emerald-700';
  };

  // 🆕 Dialog de relance de paiement
  const [relanceDialog, setRelanceDialog] = useState(null); // {dossier, sujet, contenu, email}
  const [relanceLoading, setRelanceLoading] = useState(false);
  const [relancePreview, setRelancePreview] = useState(false);
  const { previewHtml: relancePreviewHtml, loadingPreview: relanceLoadingPreview, fetchPreview: relanceFetchPreview } = useEmailPreview();

  const ouvrirRelance = async (dossier) => {
    // Préremplir un message minimal
    const jours = joursDepuisIso(dossier.date_envoi_facture_finale) ?? 0;
    const numero = dossier.numero_dossier || '';
    const contenu = `Bonjour ${(dossier.client_nom || '').split(' ')[0] || ''},\n\nNous vous remercions de la confiance accordée à Peinture Résidentielle Qc Inc.\n\nNous tenons à vous rappeler que la facture finale du projet « ${dossier.nom_dossier || ''} »${numero ? ` (#${numero})` : ''} a été envoyée il y a ${jours} jour${jours > 1 ? 's' : ''} et reste impayée à ce jour.\n\nMerci de bien vouloir procéder au règlement dans les meilleurs délais.\n\nN'hésitez pas à nous contacter pour toute question.\n\nCordialement,\nPeinture Résidentielle Qc Inc.\n(418) 264-6937`;
    setRelancePreview(false);
    setRelanceDialog({
      dossier,
      sujet: `Rappel — Facture finale impayée (${jours}j) — ${dossier.nom_dossier || ''}`,
      contenu,
      email: dossier.email_client || dossier.client_email || '',
    });
  };

  const envoyerRelance = async () => {
    if (!relanceDialog?.email || !relanceDialog?.sujet || !relanceDialog?.contenu) {
      toast.error('Sujet, contenu et destinataire requis');
      return;
    }
    setRelanceLoading(true);
    try {
      await axios.post(`${API_URL}/api/crm/dossiers/${relanceDialog.dossier.id}/relance-paiement`, {
        sujet: relanceDialog.sujet,
        contenu: relanceDialog.contenu,
        email_destinataire: relanceDialog.email,
      });
      toast.success('Relance envoyée au client !');
      setRelanceDialog(null);
      fetchDossiers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setRelanceLoading(false);
    }
  };

  const countByStatut = (s) => dossiers.filter(d => d.statut === s || (s === 'en_attente_production' && d.statut === 'attente_production')).length;


  // Calcule les jours ouvrables entre aujourd'hui et une date cible (positive = restants, négative = retard)
  const joursOuvrables = (dateStr) => {
    if (!dateStr) return null;
    const start = new Date(dateStr);
    const now = new Date();
    let count = 0;
    const d = new Date(start);
    while (d < now) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  };

  // Calcule les jours ouvrables RESTANTS avant la deadline (négatif = en retard)
  const joursOuvrableRestants = (dateStr, echeanceStr = null) => {
    if (!dateStr && !echeanceStr) return null;
    // Deadline = date_echeance explicite OU date_soum_a_faire + 5 jours ouvrables
    let deadline;
    if (echeanceStr) {
      // 🐛 Fix 2026-05-06 — echeanceStr peut arriver sous forme "YYYY-MM-DD" (input)
      // OU "YYYY-MM-DDT23:59:59" (stocké en DB par le backend). On normalise sur
      // les 10 premiers caractères pour éviter "YYYY-MM-DDT23:59:59T23:59:59" → Invalid Date.
      const dateOnly = echeanceStr.slice(0, 10);
      deadline = new Date(dateOnly + 'T23:59:59');
    } else {
      // Calculer date_soum_a_faire + 5 jours ouvrables
      deadline = new Date(dateStr);
      let added = 0;
      while (added < 5) {
        deadline.setDate(deadline.getDate() + 1);
        if (deadline.getDay() !== 0 && deadline.getDay() !== 6) added++;
      }
      deadline.setHours(23, 59, 59);
    }
    // Compter les jours ouvrables entre maintenant et la deadline
    const now = new Date();
    if (now > deadline) {
      // En retard — compter les jours ouvrables de retard
      let retard = 0;
      const d = new Date(deadline);
      while (d < now) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) retard++;
      }
      return -retard; // Négatif = retard
    } else {
      // Restant — compter les jours ouvrables restants
      let restant = 0;
      const d = new Date(now);
      while (d < deadline) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) restant++;
      }
      return restant; // Positif = jours restants
    }
  };

  // Tri par priorité pour Soum. à faire (plus urgents en premier) et Soum. à valider (plus anciens en premier)
  const dossiersTries = [...dossiersFiltres].sort((a, b) => {
    if (filtreStatut === 'soum_a_faire') {
      // Trier par jours restants : en retard en premier (valeur la plus négative), puis par urgence croissante
      const joursA = joursOuvrableRestants(a.date_soum_a_faire, a.date_echeance_soum);
      const joursB = joursOuvrableRestants(b.date_soum_a_faire, b.date_echeance_soum);
      const valA = joursA !== null ? joursA : 999;
      const valB = joursB !== null ? joursB : 999;
      return valA - valB; // Plus petit (plus négatif) en premier = plus urgent
    }
    if (filtreStatut === 'soum_a_valider') {
      const dateA = a.date_creation || '';
      const dateB = b.date_creation || '';
      return (dateA || '').localeCompare(dateB || ''); // Plus ancien en premier
    }
    return 0;
  });

  if (loading) {
    return <div className="p-6"><div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div></div></div>;
  }

  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tous les Projets</h1>
        <p className="text-gray-600 mt-1 text-sm">Vue globale de tous vos projets</p>
        {!canDo('projets_voir_tous') && user?.employee_id && (
          <p className="text-sm text-blue-600 mt-1 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 inline-block">
            Vue limitée — vous voyez uniquement vos projets assignés
          </p>
        )}
      </div>

      {/* 🆕 [Phase 85 Sprint 1.B] Légende des 3 badges — uniquement pour les
          rôles administratifs qui voient effectivement les badges. Cachée pour
          les employés terrain (peintre, chef_equipe) dont le payload est
          masqué par apply_statuts_visibility. */}
      {!isTerrainUser && <LegendeBadgesCRM />}

      {/* Filtres */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          {/* Barre de recherche — visible pour tous */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Rechercher un dossier, client, numéro..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
              </div>
            </div>
            {/* Filtres avancés — masqués pour les employés terrain */}
            {!isTerrainUser && (
              <>
                <Select value={filtreStatut} onValueChange={setFiltreStatut}>
                  <SelectTrigger><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous les statuts</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="soumission_cedulee">Soum. Cédulée</SelectItem>
                    <SelectItem value="soum_a_valider">Soum. à valider</SelectItem>
                    <SelectItem value="soum_a_faire">Soum. à faire</SelectItem>
                    <SelectItem value="soumission_envoyee">Soum. Envoyée</SelectItem>
                    <SelectItem value="suivi_a_faire">Suivi à Faire</SelectItem>
                    <SelectItem value="en_attente">En Attente</SelectItem>
                    <SelectItem value="accepte">Accepté</SelectItem>
                    <SelectItem value="refuse">Refusé</SelectItem>
                    <SelectItem value="en_attente_production">En Attente Production</SelectItem>
                    <SelectItem value="en_production">En Production</SelectItem>
                    <SelectItem value="finalise">Finalisé</SelectItem>
                    <SelectItem value="envoyer_facture">Envoyer facture</SelectItem>
                    <SelectItem value="attente_paiement">Attente paiement</SelectItem>
                    <SelectItem value="ferme">Fermé</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtreDepartement} onValueChange={setFiltreDepartement}>
                  <SelectTrigger><SelectValue placeholder="Tous les départements" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous les départements</SelectItem>
                    <SelectItem value="PRQC">PRQC</SelectItem>
                    <SelectItem value="LMDS">LMDS</SelectItem>
                    <SelectItem value="LOCATION">LOCATION</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
          {/* Filtres chef/date — masqués pour les employés terrain */}
          {!isTerrainUser && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <Select value={filtreChef} onValueChange={setFiltreChef}>
                <SelectTrigger><SelectValue placeholder="Tous les chefs d'équipe" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tous les chefs d'équipe</SelectItem>
                  {chefsUniques.map(nom => (
                    <SelectItem key={nom} value={nom}>{nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div>
                <Input type="date" value={filtreDateProd} onChange={(e) => setFiltreDateProd(e.target.value)} placeholder="Date de production" />
              </div>
              {(filtreChef !== 'tous' || filtreDateProd) && (
                <Button variant="ghost" size="sm" onClick={() => { setFiltreChef('tous'); setFiltreDateProd(''); }}>
                  Effacer filtres
                </Button>
              )}
              {/* 🆕 [Phase 17] Toggle "Inactifs uniquement" (mode exclusif — n'affiche QUE les inactifs) */}
              <label
                className={`flex items-center gap-2 text-xs cursor-pointer select-none ml-auto px-2 py-1 rounded transition-colors ${
                  showInactifs ? 'bg-red-100 text-red-700 font-semibold border border-red-300' : 'text-slate-600'
                }`}
              >
                <input
                  type="checkbox"
                  checked={showInactifs}
                  onChange={(e) => toggleShowInactifs(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="toggle-show-inactifs"
                />
                {showInactifs ? '🗄️ Vue : Projets inactifs uniquement' : 'Afficher les inactifs uniquement'}
              </label>
              {/* 🆕 [Phase 30 #6] Toggle "Projets Internes" — owner & équipe — voir SEULEMENT les BT internes */}
              <label
                className={`flex items-center gap-2 text-xs cursor-pointer select-none px-2 py-1 rounded transition-colors ${
                  internesOnly ? 'bg-indigo-100 text-indigo-700 font-semibold border border-indigo-300' : 'text-slate-600'
                }`}
              >
                <input
                  type="checkbox"
                  checked={internesOnly}
                  onChange={(e) => toggleInternesOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="toggle-internes-only"
                />
                {internesOnly ? '🏗️ Vue : Projets Internes uniquement' : '🏗️ Projets Internes'}
              </label>
            </div>
          )}

          {/* 🆕 [Phase 30 #6] Bandeau d'alerte si mode Projets Internes actif */}
          {internesOnly && (
            <div className="mb-3 px-3 py-2 bg-indigo-50 border border-indigo-300 rounded-md text-sm text-indigo-700 flex items-center gap-2" data-testid="banner-internes-only">
              <span>🏗️</span>
              <span><strong>Vue filtrée :</strong> seuls les projets internes (BT internes / entreprise) sont affichés. Décochez pour revenir à la liste normale.</span>
            </div>
          )}

          {/* 🆕 [Phase 17] Bandeau d'alerte si mode "Inactifs uniquement" actif */}
          {showInactifs && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-300 rounded-md text-sm text-red-700 flex items-center gap-2" data-testid="banner-inactifs-only">
              <span>🗄️</span>
              <span><strong>Vue filtrée :</strong> seuls les projets inactifs (abandonnés) sont affichés. Décochez pour revenir à la liste normale.</span>
            </div>
          )}

          {/* Stats — masquées pour les employés terrain */}
          {!isTerrainUser && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4 pt-4 border-t">
              {[
                { s: null, label: 'Total', color: 'text-gray-900', count: dossiers.length },
                { s: 'prospect', label: 'Prospect', color: 'text-gray-500' },
                { s: 'soumission_cedulee', label: 'Soum. Cédulée', color: 'text-cyan-600' },
                { s: 'soum_a_valider', label: 'Soum. à valider', color: 'text-pink-600' },
                { s: 'soum_a_faire', label: 'Soum. à faire', color: 'text-indigo-600' },
                { s: 'soumission_envoyee', label: 'Soum. Envoyée', color: 'text-blue-600' },
                { s: 'suivi_a_faire', label: 'Suivi à Faire', color: 'text-yellow-600' },
                { s: 'en_attente', label: 'En Attente', color: 'text-lime-600' },
                { s: 'accepte', label: 'Accepté', color: 'text-green-600' },
                { s: 'refuse', label: 'Refusé', color: 'text-red-600' },
                { s: 'en_attente_production', label: 'Attente Prod.', color: 'text-amber-600' },
                { s: 'en_production', label: 'En Production', color: 'text-orange-600' },
                { s: 'finalise', label: 'Finalisé', color: 'text-purple-600' },
                { s: 'envoyer_facture', label: 'Envoyer facture', color: 'text-fuchsia-600' },
                { s: 'attente_paiement', label: 'Attente paiement', color: 'text-rose-600' },
                { s: 'ferme', label: 'Fermé', color: 'text-emerald-600' },
              ].map((item) => (
                <div key={item.s || 'total'} className="text-center cursor-pointer hover:bg-gray-50 rounded p-1" onClick={() => setFiltreStatut(item.s || 'tous')}>
                  <p className={`text-xl font-bold ${item.color}`}>{item.count ?? countByStatut(item.s)}</p>
                  <p className="text-xs text-gray-600">{item.label}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Liste */}
      {dossiersFiltres.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              {searchTerm || filtreStatut !== 'tous' || filtreDepartement !== 'tous' ? 'Aucun dossier ne correspond aux filtres' : 'Aucun dossier pour le moment'}
            </p>
            {dossiers.length === 0 && (
              <Link to="/crm/creer-dossier"><Button><Plus className="w-4 h-4 mr-2" />Créer votre premier dossier</Button></Link>
            )}
          </CardContent>
        </Card>
      ) : showActions ? (
        /* Vue détaillée avec boutons d'action pour suivi_a_faire et en_attente */
        <div className="space-y-3">
          {dossiersTries.map((dossier) => {
            const isAFaire = dossier.statut === 'soum_a_faire';
            const isAValider = dossier.statut === 'soum_a_valider';
            const joursDepuis = isAValider ? joursOuvrables(dossier.date_creation) : null;
            // Nouveau : jours restants (négatif = en retard)
            const joursRestants = isAFaire ? joursOuvrableRestants(dossier.date_soum_a_faire, dossier.date_echeance_soum) : null;
            const enRetard = joursRestants !== null && joursRestants < 0;
            const urgenceColor = enRetard ? 'text-red-600' : joursRestants === 0 ? 'text-orange-600' : joursRestants !== null && joursRestants <= 2 ? 'text-amber-600' : 'text-green-600';
            return (
            <Card key={dossier.id} className={`hover:shadow-md transition-shadow ${enRetard ? 'border-red-300 bg-red-50/30' : joursRestants !== null && joursRestants <= 1 ? 'border-amber-300 bg-amber-50/20' : ''}`} data-testid={`dossier-action-${dossier.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {/* Infos client */}
                  <Link to={`/crm/projets/${dossier.id}`} className="flex-1 min-w-0">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <StatutsBadges dossier={dossier} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-base truncate">{dossier.client_nom}</p>
                        <p className="text-sm text-gray-600">{dossier.numero_dossier} — {dossier.nom_dossier}</p>
                        {isAFaire && joursRestants !== null && (
                          <p className={`text-xs font-semibold mt-1 ${urgenceColor}`}>
                            {enRetard
                              ? `En retard depuis ${Math.abs(joursRestants)} jour${Math.abs(joursRestants) > 1 ? 's' : ''} ouvrable${Math.abs(joursRestants) > 1 ? 's' : ''}`
                              : joursRestants === 0
                                ? `Échéance aujourd'hui !`
                                : `${joursRestants} jour${joursRestants > 1 ? 's' : ''} restant${joursRestants > 1 ? 's' : ''}`
                            }
                          </p>
                        )}
                        {isAValider && joursDepuis !== null && (
                          <p className="text-xs text-pink-600 font-medium mt-1">
                            Créée il y a {joursDepuis} jour{joursDepuis > 1 ? 's' : ''} ouvrable{joursDepuis > 1 ? 's' : ''}
                          </p>
                        )}
                        {/* 🆕 Compteur de jours pour Attente paiement */}
                        {filtreStatut === 'attente_paiement' && dossier.date_envoi_facture_finale && (() => {
                          const j = joursDepuisIso(dossier.date_envoi_facture_finale);
                          const cls = colorJoursImpayes(j);
                          const enRetardGrave = j !== null && j > 30;
                          return (
                            <p className={`text-xs mt-1 ${cls}`} data-testid={`jours-impayes-${dossier.id}`}>
                              {enRetardGrave && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                              Facture envoyée il y a {j} jour{j > 1 ? 's' : ''}
                              {dossier.nb_relances_paiement > 0 && (
                                <span className="ml-2 text-slate-500">— {dossier.nb_relances_paiement} relance{dossier.nb_relances_paiement > 1 ? 's' : ''} envoyée{dossier.nb_relances_paiement > 1 ? 's' : ''}</span>
                              )}
                            </p>
                          );
                        })()}
                      </div>
                      <div className="hidden md:flex items-center gap-4 text-sm text-gray-600">
                        {dossier.telephone_client && (
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{dossier.telephone_client}</span>
                        )}
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{dossier.ville_travaux}</span>
                        {dossier.representant_nom && (
                          <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{dossier.representant_nom}</span>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        {!isTerrainUser && dossier.montant_soumission ? (
                          <p className="font-bold text-green-700">{dossier.montant_soumission.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</p>
                        ) : null}
                        <p className="text-xs text-gray-500">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {(() => {
                            const d = dossier.date_envoi_soumission || dossier.date_creation;
                            if (!d) return '—';
                            const dt = new Date(d);
                            return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('fr-CA');
                          })()}
                        </p>
                      </div>
                    </div>
                  </Link>
                  {/* Boutons d'action */}
                  <div className="flex flex-col gap-2 flex-shrink-0 items-end">
                    {filtreStatut === 'suivi_a_faire' && (
                      <Button size="sm" variant="outline" className="border-lime-600 text-lime-700 hover:bg-lime-50" onClick={() => changerStatut(dossier.id, 'en_attente')} data-testid={`btn-attente-${dossier.id}`}>
                        <PauseCircle className="w-4 h-4 mr-1" /> Suivi fait, mettre en attente
                      </Button>
                    )}
                    {(filtreStatut === 'suivi_a_faire' || filtreStatut === 'en_attente') && (
                      <Button size="sm" variant="outline" className="border-red-600 text-red-600 hover:bg-red-50" onClick={() => changerStatut(dossier.id, 'refuse')} data-testid={`btn-refuse-${dossier.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Refusé
                      </Button>
                    )}
                    {/* Bouton "Soumission à faire" pour les Prospects */}
                    {filtreStatut === 'prospect' && (
                      <div className="flex items-center gap-2" data-testid={`soum-a-faire-${dossier.id}`}>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1.5">
                            <label className="text-xs text-slate-500">Échéance :</label>
                            <input
                              type="date"
                              value={dossiersSoumAFaire[dossier.id] || ''}
                              onChange={e => setDossiersSoumAFaire(prev => ({ ...prev, [dossier.id]: e.target.value }))}
                              className="border rounded px-2 py-1 text-xs w-36 focus:ring-indigo-300 focus:outline-none"
                              min={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => marquerSoumAFaire(dossier.id)}
                            data-testid={`btn-soum-a-faire-${dossier.id}`}
                          >
                            <FileCheck className="w-4 h-4 mr-1" /> Soumission à faire
                          </Button>
                        </div>
                      </div>
                    )}
                    {/* 🆕 Bouton "Facture prête à envoyer" pour dossiers Finalisés (CP) */}
                    {filtreStatut === 'finalise' && (
                      <Button
                        size="sm"
                        className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); facturePreteAEnvoyer(dossier.id); }}
                        disabled={facturePreteLoading[dossier.id]}
                        data-testid={`btn-facture-prete-${dossier.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        {facturePreteLoading[dossier.id] ? 'Envoi…' : 'Facture prête à envoyer'}
                      </Button>
                    )}
                    {/* 🆕 Bouton "Envoyer une relance" pour Attente paiement */}
                    {filtreStatut === 'attente_paiement' && (
                      <Button
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); ouvrirRelance(dossier); }}
                        data-testid={`btn-relance-${dossier.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Envoyer une relance
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      ) : (
        /* Vue cartes standard */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dossiersTries.map((dossier) => (
            <Link key={dossier.id} to={`/crm/projets/${dossier.id}`}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start mb-2">
                    <StatutsBadges dossier={dossier} />
                    <Badge variant="outline">{dossier.departement}</Badge>
                  </div>
                  <CardTitle className="text-lg">{dossier.nom_dossier}</CardTitle>
                  <p className="text-sm text-gray-600">{dossier.numero_dossier}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{dossier.client_nom}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{dossier.ville_travaux}, {dossier.code_postal_travaux}</span>
                      {dossier.adresse_travaux && <GoogleMapsBtn address={dossier.adresse_travaux} ville={dossier.ville_travaux} codePostal={dossier.code_postal_travaux} />}
                    </div>
                    {dossier.representant_nom && (
                      <div className="flex items-center text-gray-600">
                        <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{dossier.representant_nom}</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 pt-2">
                      Créé le {new Date(dossier.date_creation).toLocaleDateString('fr-CA')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* 🆕 Dialog Relance de paiement */}
      <Dialog open={!!relanceDialog} onOpenChange={(o) => { if (!o) setRelanceDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-relance-paiement">
          <DialogHeader>
            <DialogTitle>Relance de paiement — Aperçu</DialogTitle>
          </DialogHeader>
          {relanceDialog && (
            <div className="space-y-4">
              <div>
                <Label>Destinataire</Label>
                <Input
                  value={relanceDialog.email}
                  onChange={(e) => setRelanceDialog(d => ({ ...d, email: e.target.value }))}
                  placeholder="client@email.com"
                  data-testid="relance-email"
                />
              </div>
              <div>
                <Label>Sujet</Label>
                <Input
                  value={relanceDialog.sujet}
                  onChange={(e) => setRelanceDialog(d => ({ ...d, sujet: e.target.value }))}
                  data-testid="relance-sujet"
                />
              </div>
              <div>
                <Label>Message</Label>
                <div className="flex gap-0 mt-1">
                  <button
                    type="button"
                    className={`px-3 py-1 rounded-t border text-xs font-medium ${!relancePreview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`}
                    onClick={() => setRelancePreview(false)}
                    data-testid="relance-tab-modifier"
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 rounded-t border text-xs font-medium ${relancePreview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'}`}
                    onClick={() => { setRelancePreview(true); relanceFetchPreview(relanceDialog.contenu); }}
                    data-testid="relance-tab-apercu"
                  >
                    Aperçu
                  </button>
                </div>
                {relancePreview ? (
                  relanceLoadingPreview ? (
                    <div className="border rounded-b-md rounded-tr-md p-6 min-h-[200px] flex items-center justify-center bg-gray-50">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                    </div>
                  ) : (
                    <EmailPreviewFrame html={relancePreviewHtml} minHeight={300} />
                  )
                ) : (
                  <Textarea
                    value={relanceDialog.contenu}
                    onChange={(e) => setRelanceDialog(d => ({ ...d, contenu: e.target.value }))}
                    rows={12}
                    className="rounded-t-none"
                    data-testid="relance-contenu"
                  />
                )}
              </div>
              <p className="text-xs text-gray-500">
                Le client recevra un courriel de relance signé Peinture Résidentielle Qc Inc.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRelanceDialog(null)} data-testid="relance-btn-annuler">
                  Annuler
                </Button>
                <Button
                  onClick={envoyerRelance}
                  disabled={relanceLoading}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="relance-btn-envoyer"
                >
                  {relanceLoading ? 'Envoi…' : 'Envoyer la relance'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
